import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import "moment-timezone";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew } from "components/buttonNew";

import "./teesheet.scss";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

interface IProps {
  numberOfDays: number;
  onClick: (date: Date) => void;
  selectedDate: Date;
  showNavigation?: boolean;
  /** Keep today in the row when the week arrows move past it. The row stays the same length. */
  keepToday?: boolean;
  /** YYYY-MM-DD dates that should show a promotion indicator. */
  highlightedDates?: string[];
  onVisibleRangeChange?: (startDate: Date, endDate: Date) => void;
  bookingDragActive?: boolean;
}

function startOfDay(date: Date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return startOfDay(nextDate);
}

function isSameDay(firstDate: Date, secondDate: Date) {
  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
}

function getDatesFromStart(startDate: Date, days: number) {
  const dates = [];
  for (let i = 0; i < days; i++) {
    dates.push(addDays(startDate, i));
  }
  return dates;
}

function daysBetween(startDate: Date, endDate: Date) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(endDate).getTime() - startOfDay(startDate).getTime()) / msPerDay);
}

const PAGE_DIRECTION_ATTRIBUTE = "data-tee-sheet-page-direction";
const PAGE_STEP_MS = 650;

function pageDirectionAtPoint(clientX: number, clientY: number): -1 | 1 | null {
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    const direction = element.closest(`[${PAGE_DIRECTION_ATTRIBUTE}]`)?.getAttribute(PAGE_DIRECTION_ATTRIBUTE);
    if (direction === "-1") {
      return -1;
    }
    if (direction === "1") {
      return 1;
    }
  }
  return null;
}

function isDateInWindow(date: Date, windowStart: Date, numberOfDays: number) {
  const normalized = startOfDay(date);
  const start = startOfDay(windowStart);
  const end = addDays(start, numberOfDays - 1);
  return normalized >= start && normalized <= end;
}

function datesKeepingToday(dates: Date[], today: Date, selectedDate: Date) {
  if (dates.some(date => isSameDay(date, today))) {
    return dates;
  }

  let dropIndex = 0;
  let farthest = -1;
  dates.forEach((date, index) => {
    const distance = Math.abs(daysBetween(selectedDate, date));
    if (distance > farthest) {
      farthest = distance;
      dropIndex = index;
    }
  });

  return [...dates.filter((_, index) => index !== dropIndex), today].sort((first, second) => first.getTime() - second.getTime());
}

function getWindowStart(selectedDate: Date, numberOfDays: number) {
  const today = startOfDay(new Date());
  const selected = startOfDay(selectedDate);
  const defaultWindowEnd = addDays(today, numberOfDays - 1);

  if (selected >= today && selected <= defaultWindowEnd) {
    return today;
  }

  if (selected > defaultWindowEnd) {
    return addDays(selected, -(numberOfDays - 1));
  }

  return selected;
}

function DateBar(props: IProps) {
  const { t } = useTranslation();
  const [windowStartDate, setWindowStartDate] = useState(() => getWindowStart(props.selectedDate, props.numberOfDays));
  const skipWindowSyncRef = useRef(false);
  const barRef = useRef<HTMLDivElement>(null);
  const windowStartRef = useRef(windowStartDate);
  const restoreWindowRef = useRef<Date | null>(null);
  const suppressChevronClickRef = useRef(false);
  windowStartRef.current = windowStartDate;
  const today = startOfDay(new Date());
  const availableDates = props.keepToday
    ? datesKeepingToday(getDatesFromStart(windowStartDate, props.numberOfDays), today, props.selectedDate)
    : getDatesFromStart(windowStartDate, props.numberOfDays);

  useEffect(() => {
    if (props.bookingDragActive || restoreWindowRef.current) {
      if (!props.bookingDragActive) {
        restoreWindowRef.current = null;
      }
      return;
    }

    if (skipWindowSyncRef.current) {
      skipWindowSyncRef.current = false;
      return;
    }

    if (isDateInWindow(props.selectedDate, windowStartDate, props.numberOfDays)) {
      return;
    }

    setWindowStartDate(getWindowStart(props.selectedDate, props.numberOfDays));
  }, [props.bookingDragActive, props.selectedDate, props.numberOfDays, windowStartDate]);

  useEffect(() => {
    if (!props.bookingDragActive) {
      return;
    }

    const savedWindow = windowStartRef.current;
    restoreWindowRef.current = savedWindow;
    let direction: -1 | 1 | null = null;
    let timer: number | null = null;

    function armChevron(nextDirection: -1 | 1 | null) {
      barRef.current?.querySelectorAll(`[${PAGE_DIRECTION_ATTRIBUTE}]`).forEach(node => {
        node.querySelector(".date-bar-nav")?.classList.toggle(
          "date-bar-nav--drop-target",
          nextDirection != null && node.getAttribute(PAGE_DIRECTION_ATTRIBUTE) === String(nextDirection),
        );
      });
    }

    function clearTimer() {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function scheduleStep() {
      clearTimer();
      if (direction == null) {
        return;
      }
      const step = direction;
      timer = window.setTimeout(() => {
        setWindowStartDate(current => addDays(current, step));
        scheduleStep();
      }, PAGE_STEP_MS);
    }

    function onPointerMove(event: PointerEvent) {
      const nextDirection = pageDirectionAtPoint(event.clientX, event.clientY);
      if (nextDirection === direction) {
        return;
      }
      direction = nextDirection;
      armChevron(nextDirection);
      scheduleStep();
    }

    function onPointerUp() {
      if (direction == null) {
        return;
      }
      suppressChevronClickRef.current = true;
      window.setTimeout(() => {
        suppressChevronClickRef.current = false;
      }, 0);
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      clearTimer();
      armChevron(null);
      setWindowStartDate(savedWindow);
    };
  }, [props.bookingDragActive]);

  useEffect(() => {
    props.onVisibleRangeChange?.(windowStartDate, addDays(windowStartDate, props.numberOfDays - 1));
  }, [windowStartDate, props.numberOfDays]);

  function handleNavigate(direction: -1 | 1) {
    if (suppressChevronClickRef.current || props.bookingDragActive) {
      suppressChevronClickRef.current = false;
      return;
    }

    const slotIndex = daysBetween(windowStartDate, props.selectedDate);
    const nextWindowStart = addDays(windowStartDate, direction * props.numberOfDays);
    const nextSelectedDate = addDays(nextWindowStart, slotIndex);

    skipWindowSyncRef.current = true;
    setWindowStartDate(nextWindowStart);
    props.onClick(nextSelectedDate);
  }

  return (
    <div className="date-bar-container flex items-center min-w-0" ref={barRef}>
      {props.showNavigation && (
        <span data-tee-sheet-page-direction="-1">
          <ButtonNew
            type="tertiary"
            size="medium"
            className="date-bar-nav"
            onClick={() => handleNavigate(-1)}
            aria-label={t("Previous week")}
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
          />
        </span>
      )}
      <div className="date-bar-dates flex min-w-0">
        {availableDates.map((date, idx) => {
          const isToday = isSameDay(date, today);
          const isSelected = isSameDay(props.selectedDate, date);
          const hasPromotion = props.highlightedDates?.includes(moment(date).format("YYYY-MM-DD"));

          return (
            <div
              className={classNames("date-bar-item", {
                "date-bar-item--selected": isSelected,
                "date-bar-item--promotion": hasPromotion,
              })}
              key={`${date.getTime()}-${idx}`}
              data-tee-sheet-drop-date={moment(date).format("YYYY-MM-DD")}
              onClick={() => props.onClick(date)}
            >
              <span className="date-bar-item-label">
                {isToday ? t("Today") : moment(date).format("ddd")}
              </span>
              <span className="date-bar-item-date">
                <span className="date-bar-item-date-day">{moment(date).format("D")}</span>
                {" "}
                <span className="date-bar-item-date-month">{moment(date).format("MMM").toUpperCase()}</span>
              </span>
            </div>
          );
        })}
      </div>
      {props.showNavigation && (
        <span data-tee-sheet-page-direction="1">
          <ButtonNew
            type="tertiary"
            size="medium"
            className="date-bar-nav"
            onClick={() => handleNavigate(1)}
            aria-label={t("Next week")}
            icon={<FontAwesomeIcon icon={faChevronRight} />}
          />
        </span>
      )}
    </div>
  );
}

export default DateBar;
